import { openOverlay } from '@/features/adp/state/overlaySlice'
import ActionHeader from '@/shared/components/ActionHeader/ActionHeader'
import Pagination from '@/shared/components/Pagination/Pagination'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { MaterialIcon } from '@/ui/material-icon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/ui/tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { parseAsInteger, useQueryStates } from 'nuqs'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styles from './PipelinesList.module.scss'
import { usePipelineListQuery } from '../../queries/usePipelineQueries'
import { extractGrandTotal, hasPipelineDefinitionValue } from './pipeline-list.helpers'
import { formatDateTime } from '@/shared/utils/formatDate'
import { ScheduleDetails } from '../../services/pipeline/pipeline.types'
import { useKillJobMutation, useRunImmediateJobMutation } from '../../mutations/usePipelineMutations'
import { toast } from 'sonner'

type PipelineStatus =
  | 'finished'
  | 'stopped'
  | 'initializing'
  | 'in_progress'
  | 'failed'
  | 'not_started'

type JobMonitoringStatus =
  | 'idle'          // before any run
  | 'connecting'    // WS connecting
  | 'initializing'  // API: "Initializing"
  | 'running'       // API: "running"
  | 'finished'      // API: "finished" | "success" | "completed"
  | 'failed'        // API: "failed" | "error"
  | 'stopped'       // API: "stopped"

type PipelineRow = {
  id: string
  dataFlowId: number
  name: string
  type: string
  sessionId: string
  createdAt: string
  lastExecutedAt: string
  status: PipelineStatus
  isDownloadable: boolean
  hasQualityReport: boolean
  passedRecords?: number
  failedRecords?: number
  downloadFolderName?: string
  downloadTargetTableId?: number | null
  hasPipelineDefinition: boolean
  isSchedule: boolean;
  scheduleTypeName: string | null
  scheduleDetails?: ScheduleDetails;
}

export type PipelineScheduleTarget = Pick<
  PipelineRow,
  | 'id'
  | 'name'
  | 'sessionId'
  | 'dataFlowId'
  | 'isSchedule'
  | 'scheduleTypeName'
  | 'scheduleDetails'
>
const MAX_FREQUENCY_LABEL_LENGTH = 15

function mapApiStatusToUiStatus(apiStatus: string): PipelineStatus {
  const status = (apiStatus || '').toLowerCase().trim()
  switch (status) {
    case 'running': return 'in_progress'
    case 'finished': return 'finished'
    case 'stopped': return 'stopped'
    case 'initializing': return 'initializing'
    case 'failed': return 'failed'
    case '': return 'not_started'
    default: return 'not_started'
  }
}

function mapJobStatusToPipelineStatus(js: JobMonitoringStatus): PipelineStatus {
  switch (js) {
    case 'initializing': return 'initializing'
    case 'connecting': return 'initializing'
    case 'running': return 'in_progress'
    case 'finished': return 'finished'
    case 'failed': return 'failed'
    case 'stopped': return 'stopped'
    default: return 'not_started'
  }
}

function truncateFrequencyLabel(value: string): string {
  if (value.length <= MAX_FREQUENCY_LABEL_LENGTH) return value
  return `${value.slice(0, MAX_FREQUENCY_LABEL_LENGTH)}...`
}

function StatusBadge({ status }: Readonly<{ status: PipelineStatus }>) {
  switch (status) {
    case 'finished':
      return (
        <span className={`${styles.statusPill} ${styles.statusFinished}`}>
          <MaterialIcon name="check" size={14} />
          Finished
        </span>
      )
    case 'stopped':
      return (
        <span className={`${styles.statusPill} ${styles.statusStopped}`}>
          <MaterialIcon name="stop" size={14} />
          Stopped
        </span>
      )
    case 'initializing':
      return (
        <span className={`${styles.statusPill} ${styles.statusInitializing}`}>
          <MaterialIcon name="schedule" size={14} />
          Initializing
        </span>
      )
    case 'in_progress':
      return (
        <span className={`${styles.statusPill} ${styles.statusInProgress}`}>
          <MaterialIcon name="sync" size={14} />
          In Progress
        </span>
      )
    case 'failed':
      return (
        <span className={`${styles.statusPill} ${styles.statusFailed}`}>
          <MaterialIcon name="error" size={14} />
          Failed
        </span>
      )
    case 'not_started':
    default:
      return (
        <span className={`${styles.statusPill} ${styles.statusNotStarted}`}>
          <MaterialIcon name="radio_button_unchecked" size={14} />
          Not Started
        </span>
      )
  }
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'running', label: 'Running' },
  { value: 'Initializing', label: 'Initializing' },
  { value: 'finished', label: 'Finished' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'failed', label: 'Failed' },
]

type ActionState = {
  iconName: string
  actionLabel: string
  ariaLabel: string
}

function getActionState(status: PipelineStatus, pipelineName: string): ActionState {
  if (status === 'initializing' || status === 'in_progress') {
    return { iconName: 'stop_circle', actionLabel: 'Stop', ariaLabel: `Stop ${pipelineName}` }
  }
  if (status === 'finished' || status === 'failed') {
    return { iconName: 'refresh', actionLabel: 'Re-Run', ariaLabel: `Re-run ${pipelineName}` }
  }
  return { iconName: 'play_arrow', actionLabel: 'Run', ariaLabel: `Run ${pipelineName}` }
}

function StatusBasedActionButton({
  status,
  sessionId,
  isLoading: externalIsLoading,
  onRunImmediate,
  onStopPipeline,
  pipelineName,
  onLoadingChange,
}: Readonly<{
  status: PipelineStatus
  sessionId: string
  isLoading: boolean
  onRunImmediate?: (sessionId: string) => Promise<void> | void
  onStopPipeline?: (sessionId: string) => Promise<void> | void
  pipelineName: string
  onLoadingChange?: (isLoading: boolean) => void
}>) {
  const isActionDisabled = externalIsLoading;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!sessionId) return
    onLoadingChange?.(true);

    try {
      if (status === 'initializing' || status === 'in_progress') {
        await onStopPipeline?.(sessionId);
        toast.success('Pipeline stopped successfully');
      } else {
        await onRunImmediate?.(sessionId);
        toast.success('Pipeline triggered successfully');
      }
    } catch {
      /*empty*/
    } finally {
      onLoadingChange?.(false)
    }
  }

  const { iconName, actionLabel, ariaLabel } = getActionState(status, pipelineName)

  return (
    <button
      type="button"
      className={cn(styles.iconAction, styles.iconActionWithLabel)}
      aria-label={ariaLabel}
      disabled={isActionDisabled}
      onClick={handleClick}
      title={actionLabel}
    >
      <MaterialIcon name={iconName} size={20} />
      <span className={styles.actionLabel}>{actionLabel}</span>
    </button>
  )
}

function RowActionsMenu({
  pipeline,
  status,
  isDownloadable,
  hasQualityReport,
  downloadFolderName,
  downloadTargetTableId,
  passedRecords,
  failedRecords,
}: Readonly<{
  pipeline: PipelineScheduleTarget
  status: PipelineStatus
  isDownloadable: boolean
  hasQualityReport: boolean
  downloadFolderName?: string
  downloadTargetTableId?: number | null
  passedRecords?: number
  failedRecords?: number
}>) {
  const hasNoRuns = status === 'not_started'
  const dispatch = useDispatch()

  const handleDownloadOverlayOpen = () => {
    dispatch(
      openOverlay({
        type: 'download-output-modal',
        data: {
          pipelineName: pipeline.name,
          downloadFolderName: downloadFolderName ?? null,
          downloadTargetTableId: downloadTargetTableId ?? null,
        },
      })
    )
  }

  const handleDeletePipeline = () => {
    dispatch(
      openOverlay({
        type: 'delete-pipeline-dialog',
        data: {
          pipelineId: pipeline.id,
          pipelineName: pipeline.name,
          source: 'pipeline-list',
        },
      })
    )
  }

  const handleScheduleOverlayOpen = () => {
    console.log('pipeline', pipeline);
    dispatch(
      openOverlay({
        type: 'schedule-pipeline-modal',
        data: {
          pipelineName: pipeline.name,
          dataFlowId: pipeline.dataFlowId,
          sessionId: pipeline.sessionId,
          isSchedule: pipeline.isSchedule,
          scheduleDetails: pipeline.scheduleDetails,
          scheduleTypeName: pipeline.scheduleTypeName ?? null,
          persistScheduleInPipelineStore: false,
        },
      })
    )
  }

  const handleQualityReport = () => {
    dispatch(
      openOverlay({
        type: 'dq-report-modal',
        data: {
          pipelineId: pipeline.id,
          pipelineName: pipeline.name,
          passedRecords,
          failedRecords,
        },
      })
    )
  }

  const handleViewRunHistory = () => {
    dispatch(
      openOverlay({
        type: 'run-history-modal',
        data: {
          pipelineId: pipeline.id,
          pipelineName: pipeline.name,
          dataFlowId: pipeline.dataFlowId,
        },
      })
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={styles.iconAction} aria-label="More actions">
          <MaterialIcon name="more_vert" size={20} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className={styles.dropdownMenu}>
        <DropdownMenuItem
          className={styles.dropdownItem}
          onSelect={handleViewRunHistory}
          disabled={hasNoRuns}
        >
          <MaterialIcon name="history" size={18} />
          <span>View Run History</span>
        </DropdownMenuItem>

        {hasQualityReport && (
          <DropdownMenuItem className={styles.dropdownItem} onSelect={handleQualityReport}>
            <MaterialIcon name="assessment" size={18} />
            <span>Quality Report</span>
          </DropdownMenuItem>
        )}

        {isDownloadable && (
          <DropdownMenuItem
            className={styles.dropdownItem}
            onSelect={handleDownloadOverlayOpen}
          >
            <MaterialIcon name="download" size={18} />
            <span>Download</span>
          </DropdownMenuItem>
        )}

        {pipeline.isSchedule ? (
          <DropdownMenuItem className={styles.dropdownItem} onSelect={handleScheduleOverlayOpen}>
            <MaterialIcon name="edit" size={18} />
            <span>Edit Pipeline Schedule</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className={styles.dropdownItem} onSelect={handleScheduleOverlayOpen}>
            <MaterialIcon name="schedule" size={18} />
            <span>Create Pipeline Schedule</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className={styles.separator} />

        <DropdownMenuItem
          className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
          onSelect={handleDeletePipeline}
        >
          <MaterialIcon name="delete" size={18} />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}


export function PipelinesList() {
  const queryClient = useQueryClient()
  const runImmediateJobMutation = useRunImmediateJobMutation()
  const killJobMutation = useKillJobMutation()
  const [{ page, pageSize }, setPagination] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(10),
    },
    { history: 'push' }
  )
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [spinningRows, setSpinningRows] = useState<Record<string, boolean>>({})

  const size = pageSize
  const apiStatusParam = statusFilter === 'all' ? undefined : statusFilter

  const { data, isLoading, error, isError } = usePipelineListQuery(
    { page, size, search: search.trim() || undefined, status: apiStatusParam })

  const totalPages = Math.max(1, Math.ceil(data?.totalCount ?? 0 / size))

  useEffect(() => {
    void setPagination((prev) => ({ ...prev, page: Math.min(prev.page, totalPages) }))
  }, [totalPages])

  const displayPage = Math.min(page, totalPages)
  const liveStatuses = { id: 1 }
  const liveMessages = { id: 1 }

  const pipelineRows: PipelineRow[] = useMemo(
    () =>
      data?.pipelines?.map((item) => {
        const baseStatus = mapApiStatusToUiStatus(item.status)
        const liveJobStatus = liveStatuses[item.data_flow_id]
        const liveMsg = liveMessages[item.data_flow_id]

        const resolvedStatus = liveJobStatus
          ? mapJobStatusToPipelineStatus(liveJobStatus)
          : baseStatus

        const lastExecutedRaw = liveMsg?.last_executed ?? item.last_modified_datetime_timestamp
        const dqPassedRaw = liveMsg?.total_dq_passed_records ?? item.total_dq_passed_records
        const dqFailedRaw = liveMsg?.total_dq_failed_records ?? item.total_dq_failed_records
        const hasQualityReport =
          !(liveMsg?.total_dq_passed_records == null && liveMsg?.total_dq_failed_records == null) ||
          !(item.total_dq_passed_records == null && item.total_dq_failed_records == null)

        return {
          id: String(item.data_flow_id),
          dataFlowId: item.data_flow_id,
          name: item.data_flow_name,
          type: item.data_flow_type,
          sessionId: item.session_id,
          createdAt: formatDateTime(item.created_datetime_timestamp),
          lastExecutedAt: formatDateTime(lastExecutedRaw),
          status: resolvedStatus,
          isDownloadable: Boolean(item.is_downloadable),
          hasQualityReport,
          passedRecords: extractGrandTotal(dqPassedRaw),
          failedRecords: extractGrandTotal(dqFailedRaw),
          downloadFolderName: item.folder_name ?? undefined,
          downloadTargetTableId: item.target_table_id,
          hasPipelineDefinition: hasPipelineDefinitionValue(item.pipeline_definition),
          isSchedule: Boolean(item.is_schedule),
          scheduleTypeName: item.schedule_type_name ?? null,
          scheduleDetails: item.schedule_details ?? null,
        }
      }),
    [data?.pipelines, liveStatuses, liveMessages]
  )

  const handleStatusSelect = (value: string) => {
    setStatusFilter(value)
    void setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleRefreshPipelines = () => {
    void queryClient.invalidateQueries({ queryKey: ['pipeline-list'] })
  }

  let tableBodyContent: React.ReactNode
  if (isLoading) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={7} className={styles.loadingCell}>Loading pipelines...</TableCell>
      </TableRow>
    )
  } else if (isError) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={7} className={styles.errorCell}>
          Error loading pipelines: {error?.message}
        </TableCell>
      </TableRow>
    )
  } else if (pipelineRows?.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={7} className={styles.emptyCell}>No pipelines found.</TableCell>
      </TableRow>
    )
  } else {
    tableBodyContent = pipelineRows?.map((row, idx) => (
      <TableRow key={row.id} className={idx % 2 === 1 ? styles.rowAlt : undefined}>
        <TableCell className={styles.cellStrong}>
          {row.name.length > 25 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={styles.pipelineNameText}>{row.name}</span>
              </TooltipTrigger>
              <TooltipContent side="top">{row.name}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={styles.pipelineNameText}>{row.name}</span>
          )}
        </TableCell>
        <TableCell className={styles.cellMuted}>{row.createdAt}</TableCell>
        <TableCell className={styles.cellMuted}>{row.lastExecutedAt}</TableCell>
        <TableCell className={styles.statusCell}>
          <StatusBadge status={row.status} />
        </TableCell>
        <TableCell className={styles.cellMuted}>
          {row.isSchedule ? 'Scheduled' : 'Immediate'}
        </TableCell>
        <TableCell className={styles.cellMuted}>
          {row.scheduleTypeName && row.scheduleTypeName.length > MAX_FREQUENCY_LABEL_LENGTH ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={styles.frequencyValue}>
                  {truncateFrequencyLabel(row.scheduleTypeName)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{row.scheduleTypeName}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={styles.frequencyValue}>{row.scheduleTypeName ?? '-'}</span>
          )}
        </TableCell>
        <TableCell>
          <div className={styles.actionsCell}>
            <StatusBasedActionButton
              status={row.status}
              sessionId={row.sessionId}
              isLoading={
                Boolean(spinningRows[row.id]) ||
                runImmediateJobMutation.isPending ||
                killJobMutation.isPending
              }
              onRunImmediate={async (id) => {
                await runImmediateJobMutation.mutateAsync(id)
              }}
              onStopPipeline={async (id) => {
                await killJobMutation.mutateAsync(id)
              }}
              pipelineName={row.name}
              onLoadingChange={(loading) =>
                setSpinningRows((prev) => ({ ...prev, [row.id]: loading }))
              }
            />
            <RowActionsMenu
              pipeline={row}
              status={row.status}
              isDownloadable={row.isDownloadable}
              hasQualityReport={row.hasQualityReport}
              downloadFolderName={row.downloadFolderName}
              downloadTargetTableId={row.downloadTargetTableId}
              passedRecords={row.passedRecords}
              failedRecords={row.failedRecords}
            />
          </div>
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <section className={styles.pipelineListSection}>
      <header className={styles.pageHeader}>
        <ActionHeader
          className={styles.pipelinesActionHeader}
          title="Pipelines List"
          leadingActions={[
            {
              icon: 'cached',
              variant: 'icon',
              onClick: handleRefreshPipelines,
              ariaLabel: 'refresh-pipelines',
              key: 'refresh-pipelines',
            },
          ]}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search Pipeline"
          searchInputClassName={styles.pipelinesSearchInput}
          actions={[
            {
              label: 'New Pipeline',
              icon: 'add',
              onClick: () => { },
              ariaLabel: 'new-pipeline',
              key: 'new-pipeline',
            },
          ]}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Filter pipelines"
                className={styles.filterIconBtn}
              >
                <MaterialIcon name="filter_list" size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={styles.filterDropdownMenu}>
              <div className={styles.filterDropdownHeader}>
                <MaterialIcon name="filter_list" size={16} />
                <span>Filter by Status</span>
              </div>
              {STATUS_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  className={`${styles.filterDropdownItem} ${statusFilter === option.value ? styles.filterDropdownItemActive : ''
                    }`}
                  onSelect={() => handleStatusSelect(option.value)}
                >
                  <span>{option.label}</span>
                  {statusFilter === option.value && <MaterialIcon name="check" size={16} />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </ActionHeader>
      </header>

      <div className={styles.tableScroll}>
        <Table className={styles.dataTable}>
          <TableHeader>
            <TableRow className={styles.headerRow}>
              <TableHead className={styles.colName}>Name</TableHead>
              <TableHead className={styles.colDate}>Created Date</TableHead>
              <TableHead className={styles.colDate}>Last Executed Date</TableHead>
              <TableHead className={styles.colStatus}>Status</TableHead>
              <TableHead className={styles.colRunTime}>Run Type</TableHead>
              <TableHead className={styles.colFrequency}>Frequency</TableHead>
              <TableHead className={styles.colActions}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{tableBodyContent}</TableBody>
        </Table>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLeft} />
        <div className={styles.footerRight}>
          <Pagination
            currentPage={displayPage}
            totalPages={totalPages}
            rowsPerPage={size}
            onPageChange={(n) => void setPagination((prev) => ({ ...prev, page: n }))}
            onRowsPerPageChange={(n) => {
              void setPagination({ page: 1, pageSize: n })
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      </div>
    </section>
  )
}
